import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import BoardCard from "@/components/BoardCard";
import { Button } from "@/components/ui/button";
import { Plus, Layers } from "lucide-react";
import { useState } from "react";
import CreateBoardDialog from "@/components/dialogs/CreateBoardDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

export default function WorkspaceView() {
  const { workspaceId } = useParams();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}`)).data,
  });
  const { data: boards = [], isLoading } = useQuery({
    queryKey: ["boards", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/boards`)).data,
  });

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10" data-testid="workspace-view">
      <div className="max-w-7xl mx-auto space-y-8 animate-fade-in-up">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("workspace.eyebrow")}</p>
            <h1 className="font-display text-4xl font-semibold tracking-tight">{workspace?.name || "…"}</h1>
            {workspace?.description && <p className="text-muted-foreground mt-2 max-w-xl">{workspace.description}</p>}
          </div>
          <Button onClick={() => setOpen(true)} data-testid="workspace-create-board" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30">
            <Plus className="h-4 w-4 mr-2" /> {t("workspace.newBoard")}
          </Button>
        </div>

        <section>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
            </div>
          ) : boards.length === 0 ? (
            <div className="glass-panel rounded-3xl p-10 text-center">
              <Layers className="h-10 w-10 text-blue-500 mx-auto mb-3" />
              <p className="font-display text-lg">{t("workspace.emptyTitle")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("workspace.emptySub")}</p>
              <Button onClick={() => setOpen(true)} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="workspace-empty-create-board">
                <Plus className="h-4 w-4 mr-2" /> {t("workspace.createBoard")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {boards.filter(b => !b.is_archived).map((b) => <BoardCard key={b.board_id} board={b} />)}
            </div>
          )}
        </section>
      </div>
      <CreateBoardDialog open={open} onOpenChange={setOpen} defaultWorkspaceId={workspaceId} />
    </div>
  );
}
